import { defineComponent, reactive, SetupContext, readonly, ref, onMounted } from '@vue/composition-api';
import { InformationDeleteType, AttachmentFile } from '@/shared/type';
import RichTextEditor from '../common/rich-text-editor.component.vue';
import vueRouter from '@/router/index';
import SystemConst from '@/shared/system-const';
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';
import CommonFunction from '@/shared/common-function';
import MessageConst from '@/shared/message-const';
import HttpStatus from 'http-status-codes';

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };                               // Windowサイズ
    information: InformationDeleteType;                                 // お知らせフォーム
    createMethodDisplay: string;                                        // 作成形式（表示用）
    publicationOrganizationTypeDisplay: string;                         // 公開先の組織型（表示用)
    publicationOrganizationIdDisplay: string;                           // 公開先の組織（表示用)
    filesDisplay: AttachmentFile[];                                     // 添付ファイル（表示用）
}

/**
 * お知らせ削除画面コンポーネント
 */
export default defineComponent({
    components: { CommonMessageDialog, RichTextEditor },
    setup(props, ctx: SetupContext) {
        const form = ref<any>(null);
        const dateRef = ref<any>(null);
        const commonRef = ref<any>(null);                               // メッセージダイアログ
        const router = vueRouter;                                       // ルータ
        const createStyleSet = {
            // 作成形式（選択）の値
            simple: SystemConst.CREATE_METHOD_TEXT.SIMPLE,              // 簡易登録
            link: SystemConst.CREATE_METHOD_TEXT.LINK                   // リンクURL登録
        };
        const formHeight = SystemConst.FORM_HEIGHT.INFORMATION;         // フォーム高さ調整用変数
        const publicationRange = {
            // 公開範囲（選択）の値
            selectOrganizationType:
                SystemConst.PUBLICATION_RANGE_TEXT.
                    SELECT_ORGANIZATION_TYPE,                           // 指定した組織種類の全教職員アカウント（一般を含む）
            selectOrganization:
                SystemConst.PUBLICATION_RANGE_TEXT.
                    SELECT_ORGANIZATION                                 // 指定した組織の全教職員アカウント（一般を含む）

        };
        // リアクティブな変数定義
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },                                 // ウィンドウサイズ
            information: {
                title: '',
                publishStartDate: '',
                publishStartDateDisplay: '',
                publishEndDate: '',
                publishEndDateDisplay: '',
                severityName: '',
                publicationOrganizationTypeName: [],
                publicationOrganizationName: [],
                publicationRangeName: '',
                sender: '',
                url: '',
                createMethodName: SystemConst.CREATE_METHOD_TEXT.SIMPLE,
                files: [],
                body: ''
            },
            publicationOrganizationTypeDisplay: '',                      // 公開先の組織型（表示用)
            publicationOrganizationIdDisplay: '',                        // 公開先の組織（表示用)
            createMethodDisplay: '',                                     // 作成形式（表示用）
            filesDisplay: []                                             // 添付ファイル（表示用）
        });
        let informationId = '';                                          // 削除対象のお知らせID

        /**
         * 初期処理
         */
        onMounted(() => {
            init();
        })

        async function init() {

            // お知らせ更新/削除対象取得APIで初期表示データを取得する
            // パラメータ:お知らせID
            const response = await ctx.root.$store.dispatch('information/getOperateInformation',
                {
                    informationId: informationId
                }
            );

            // HTTPステータスコードが200以外の場合
            if (response.status !== HttpStatus.OK) {
                // HTTPステータスコードが200以外の場合
                if (response.status === HttpStatus.NOT_FOUND) {
                    /// HTTPステータスコードが404の場合、displayErrorMessageTransitionDialogメソッドで
                    // （管理向け）お知らせ一覧画面へ戻る
                    commonRef.value.displayErrorMessageTransitionDialog(response, '/information/list');
                } else {
                    // HTTPステータスコードが200, 404以外の場合
                    // errorDisplayTransitionControlメソッドでエラー画面へ遷移して処理を終了する
                    commonRef.value.errorDisplayTransitionControl(response);
                }
                return;
            }

            // HTTPステータスコードが200の場合
            // 取得したデータを画面へ設定する
            state.information = response.data.information;

            // 作成形式を作成形式（表示用）に設定する
            state.createMethodDisplay = state.information.createMethodName;

            // 日付を表示用にYYYY-MM-DDからYYYY/MM/DDにフォーマット変換し、
            // stateのpublishStartDateDisplay、publishEndDateDisplayに設定する
            state.information.publishStartDateDisplay = state.information.publishStartDate.replaceAll('-', '/');
            state.information.publishEndDateDisplay = state.information.publishEndDate.replaceAll('-', '/');

            // 取得したpublicationOrganizationTypeName分、
            // publicationOrganizationTypeDisplayにカンマ区切りでデータを格納する
            if (state.information.publicationOrganizationTypeName !== void 0) {
                state.information.publicationOrganizationTypeName.forEach((element, index) => {
                    if (index !== 0) {
                        state.publicationOrganizationTypeDisplay += ',';
                    }
                    state.publicationOrganizationTypeDisplay += element;
                });
            }

            // 取得したpublicationOrganizationName分、publicationOrganizationIdDisplayに
            // 【publicationOrganizationTypeName】publicationOrganizationNameの形式で、
            // カンマ区切りでデータを格納する
            if (state.information.publicationOrganizationName !== void 0) {
                for (let index = 0; index < state.information.publicationOrganizationName.length; index++) {
                    if (index !== 0) {
                        state.publicationOrganizationIdDisplay += ',';
                    }
                    state.publicationOrganizationIdDisplay +=
                        '【' + state.information.publicationOrganizationTypeName[index] +
                        '】' + state.information.publicationOrganizationName[index];
                }
            }

            // 作成方式が'簡易登録'で、添付ファイルを取得している場合
            if (state.information.createMethodName
                === createStyleSet['simple'] && state.information.files.length !== 0) {
                // 添付ファイルのサイズを「B(バイト)」表記から「KB(キロバイト)」表記へ変換する
                state.information.files.forEach(element => {
                    const tmpFiles: AttachmentFile[] = [         // 一時保存する添付ファイル情報
                        {
                            fileIcon: '',
                            fileSequence: 0,
                            fileName: '',
                            fileSize: ''
                        }
                    ]
                    // KBへ変換し、小数第三位まで表示する
                    const conversionFileSize = String(Math.round((element.fileSize / 1024) * 1000) / 1000);
                    tmpFiles[0].fileName = element.fileName;
                    tmpFiles[0].fileSize = conversionFileSize;
                    // 添付ファイル（表示用）に設定する
                    state.filesDisplay.push(tmpFiles[0]);
                });
            }
        }

        /**
         * 初期表示
         */
        created();
        async function created() {

            // お知らせIDの取得
            informationId = ctx.root.$route.params.id;

            // titleIconを'mdi-text-box-remove-outline'に変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-text-box-remove-outline');

            // mainTitleを「お知らせ削除」に変更
            ctx.root.$store.commit('setMainTitle', 'お知らせ削除');
        }

        /**
         * 削除ボタン押下時に確認メッセージダイアログ表示
         */
        function displayDialog() {
            // 確認メッセージダイアログ表示
            const displayMessage = CommonFunction.replaceArray(MessageConst.S_CONFIRMATION, ['削除']);
            commonRef.value.displayConfirmationMessageDialog(displayMessage);
        }

        /**
         * 削除画面にて、確認メッセージダイアログの「はい」を押下時の処理
         */
        async function processingData() {
            // お知らせ削除APIを呼び出す
            // パラメータ: お知らせID
            const response = await ctx.root.$store.dispatch('information/deleteInformation',
                {
                    'informationId': informationId
                }
            );
            // 通信結果判別
            if (response.status !== HttpStatus.OK) {
                // HTTPステータスコードが200以外の場合
                if (response.status === HttpStatus.NOT_FOUND) {
                    //  HTTPステータスコードが404の場合
                    //  displayErrorMessageTransitionDialogメソッドでお知らせ一覧画面に遷移
                    commonRef.value.displayErrorMessageTransitionDialog(response, '/information/list');
                } else {
                    //  HTTPステータスコードが404以外の場合
                    //  errorDisplayTransitionControlメソッドでエラー画面を表示する
                    commonRef.value.errorDisplayTransitionControl(response);
                }
                return;
            }
            // HTTPステータスコードが200の場合
            //  削除完了メッセージダイアログを表示してお知らせ一覧画面に遷移
            commonRef.value.displayMessageTransitionDialog(response, '/information/list');
        }

        /**
         * キャンセルボタン押下時の処理
         */
        function back() {
            // storeのページの最終データフラグをfalseに設定する
            ctx.root.$store.commit('information/setIsPageLastData', false);

            // storeのページ番号保持フラグをtrueに設定する
            ctx.root.$store.state.information.isSavePage = true;

            // お知らせ一覧に遷移する
            router.push('/information/list');
        }
        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
        }

        return {
            form,
            dateRef,
            commonRef,
            createStyleSet,
            publicationRange,
            formHeight,
            state: readonly(state),
            displayDialog,
            processingData,
            back,
            onResize
        };
    }
});
